import React, { useState, useEffect, useRef, useCallback } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { CoreButton, CoreSpinner } from "@verity/components/core";
import { ThemeWrapper } from "@verity/components";

const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const PAGE_MARGIN_LEFT = 29;
const PAGE_MARGIN_TOP = 21;

interface AudioTranscriptPDFGeneratorProps {
	component: ReturnType<typeof AudioTranscriptReport>;
	autoGenerate?: boolean;
	onComplete?: () => void;
}

const AudioTranscriptPDFGenerator = ({
	component,
	autoGenerate = false,
	onComplete,
}: AudioTranscriptPDFGeneratorProps) => {
	const [buttonText, setButtonText] = useState("Generate Audio Transcript");
	const [state, setState] = useState<"notGenerated" | "generating" | "downloadReady" | "downloading">("notGenerated");

	useEffect(() => {
		if (state === "notGenerated") {
			setButtonText("Generate Audio Transcript");
		} else if (state === "generating") {
			setButtonText("Generating");
		} else if (state === "downloadReady") {
			setButtonText("Download Audio Transcript");
		} else if (state === "downloading") {
			setButtonText("Downloading");
		}
	}, [state]);

	const handleButtonClick = async () => {
		if (state === "notGenerated") {
			setState("generating");
			setTimeout(() => {
				setState("downloadReady");
			}, 500);
			return;
		}

		if (state === "downloadReady") {
			setState("downloading");

			const element = document.getElementById("audio-transcript-to-pdf");
			if (element) {
				const canvas = await html2canvas(element, {
					useCORS: true,
					scale: 2,
					backgroundColor: "#FFFFFF",
				});
				const imgData = canvas.toDataURL("image/png");

				const pdf = new jsPDF({
					orientation: "portrait",
					unit: "px",
					format: [PAGE_WIDTH, PAGE_HEIGHT],
					compress: false,
				});

				const imgProps = pdf.getImageProperties(imgData);
				const pdfWidth = pdf.internal.pageSize.getWidth();
				const pdfHeight = pdf.internal.pageSize.getHeight();
				const pdfImageWidth = pdfWidth;
				const pdfImageHeight = Math.floor((imgProps.height * pdfImageWidth) / imgProps.width);

				let heightLeft = pdfImageHeight;
				let position = 0;

				pdf.addImage(imgData, "PNG", 0, position, pdfImageWidth, pdfImageHeight);
				heightLeft -= pdfHeight;
				position -= pdfHeight;

				// Use epsilon to handle floating point precision issues
				while (heightLeft > 1) {
					pdf.addPage();
					pdf.addImage(imgData, "PNG", 0, position, pdfImageWidth, pdfImageHeight);
					heightLeft -= pdfHeight;
					position -= pdfHeight;
				}

				const studentName = (component?.props as AudioTranscriptReportProps | undefined)?.studentName || "student";

				const fileName = `${studentName.toLowerCase().replaceAll(" ", "-")}_audio_transcript.pdf`;
				pdf.save(fileName);
			}

			setState("downloadReady");
			if (onComplete) {
				onComplete();
			}
		}
	};

	useEffect(() => {
		if (autoGenerate && state === "notGenerated") {
			void handleButtonClick();
		} else if (autoGenerate && state === "downloadReady") {
			void handleButtonClick();
		}
	}, [autoGenerate, state]);

	const enhancedComponent = React.cloneElement(component, {});

	return (
		<>
			{state !== "notGenerated" && (
				<div
					style={{
						width: `${PAGE_WIDTH}px`,
						left: "-1000000px",
						position: "absolute",
					}}
				>
					{enhancedComponent}
				</div>
			)}
			<CoreButton
				disabled={state === "downloading" || state === "generating"}
				onClick={handleButtonClick}
				endIcon={state === "generating" || state === "downloading" ? <CoreSpinner size={13} /> : undefined}
			>
				{buttonText}
			</CoreButton>
		</>
	);
};

interface AudioTranscriptReportProps {
	courseName: string;
	assessmentName: string;
	studentName: string;
	attemptNumber: string;
	assessmentStatus: string;
	recordingDate: string;
	transcripts: string[];
}
const AudioTranscriptReport = (props: AudioTranscriptReportProps) => {
	const HEADER_HEIGHT = 74;
	const FOOTER_HEIGHT = 72;
	const FIRST_PAGE_HEADER_SECTION = PAGE_MARGIN_TOP + HEADER_HEIGHT + 42 + 20 + 30 + 144 + 30;
	const SUBSEQUENT_PAGE_HEADER_SECTION = PAGE_MARGIN_TOP + HEADER_HEIGHT + 42;
	const FIRST_PAGE_MAX_CONTENT_HEIGHT = PAGE_HEIGHT - FIRST_PAGE_HEADER_SECTION - FOOTER_HEIGHT;
	const SUBSEQUENT_PAGE_MAX_CONTENT_HEIGHT = PAGE_HEIGHT - SUBSEQUENT_PAGE_HEADER_SECTION - FOOTER_HEIGHT;

	const measureRef = useRef<HTMLDivElement>(null);
	const [pages, setPages] = useState<Array<{ content: string; pageNum: number; isFirstPage: boolean }>>([]);

	const getConcatenatedTranscript = useCallback(() => {
		const concatenatedTranscript = props.transcripts.join(" ");
		if (concatenatedTranscript === "") {
			// this can happen if the recording is very short, and no words were spoken
			// pad in this case to avoid empty pages
			return "\t";
		}
		return concatenatedTranscript;
	}, [props.transcripts]);

	useEffect(() => {
		if (!measureRef.current) return;

		const splitTextIntoPages = () => {
			const tempPages: Array<{ content: string; pageNum: number; isFirstPage: boolean }> = [];
			let remainingText = getConcatenatedTranscript();
			let pageNum = 1;
			let isFirstPage = true;

			while (remainingText.length > 0) {
				const maxHeight = isFirstPage ? FIRST_PAGE_MAX_CONTENT_HEIGHT : SUBSEQUENT_PAGE_MAX_CONTENT_HEIGHT;

				if (measureRef.current) {
					measureRef.current.textContent = remainingText;
					const fullHeight = measureRef.current.scrollHeight;

					if (fullHeight <= maxHeight) {
						tempPages.push({ content: remainingText, pageNum, isFirstPage });
						break;
					}

					let low = 0;
					let high = remainingText.length;
					let bestFit = Math.floor(remainingText.length / 2);

					while (low <= high) {
						const mid = Math.floor((low + high) / 2);
						const testText = remainingText.substring(0, mid);
						measureRef.current.textContent = testText;
						const testHeight = measureRef.current.scrollHeight;

						if (testHeight <= maxHeight) {
							bestFit = mid;
							low = mid + 1;
						} else {
							high = mid - 1;
						}
					}

					let breakPoint = bestFit;
					const lastSpace = remainingText.lastIndexOf(" ", bestFit);
					if (lastSpace > bestFit * 0.9) {
						breakPoint = lastSpace + 1;
					}

					const pageContent = remainingText.substring(0, breakPoint).trim();
					tempPages.push({ content: pageContent, pageNum, isFirstPage });
					remainingText = remainingText.substring(breakPoint).trim();
					pageNum++;
					isFirstPage = false;
				}
			}

			setPages(tempPages);
		};

		splitTextIntoPages();
	}, [
		getConcatenatedTranscript,
		FIRST_PAGE_MAX_CONTENT_HEIGHT,
		SUBSEQUENT_PAGE_MAX_CONTENT_HEIGHT,
		props.transcripts.length,
	]);

	return (
		<ThemeWrapper>
			<div
				ref={measureRef}
				style={{
					position: "absolute",
					left: "-9999px",
					width: `${PAGE_WIDTH - PAGE_MARGIN_LEFT * 2}px`,
					fontFamily: "Inter",
					fontWeight: 400,
					fontSize: "12px",
					lineHeight: "1.5em",
					whiteSpace: "pre-wrap",
					wordWrap: "break-word",
					visibility: "hidden",
				}}
			/>
			<div id="audio-transcript-to-pdf" style={{ background: "#FFFFFF", lineHeight: 0, fontSize: 0 }}>
				{pages.map((page) => (
					<div
						key={page.pageNum}
						style={{
							width: `${PAGE_WIDTH}px`,
							height: `${PAGE_HEIGHT}px`,
							background: "#FFFFFF",
							fontFamily: "Inter, sans-serif",
							margin: "0",
							padding: "0",
							display: "flex",
							flexDirection: "column",
							pageBreakAfter: page.pageNum < pages.length ? "always" : "auto",
						}}
					>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								gap: "1.8px",
								paddingTop: "21px",
								flexShrink: 0,
							}}
						>
							<img
								src={new URL("./logo.svg", import.meta.url).href}
								alt="YuJa Verity Logo"
								style={{
									width: "32.34px",
									height: "32.34px",
								}}
							/>
							<div
								style={{
									fontFamily: "Inter",
									fontWeight: 600,
									fontSize: "12.58px",
									lineHeight: "1.5em",
									color: "#333333",
								}}
							>
								YuJa Verity
							</div>
						</div>

						<div
							style={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								paddingLeft: `${PAGE_MARGIN_LEFT}px`,
								paddingRight: `${PAGE_MARGIN_LEFT}px`,
								paddingTop: "42px",
								overflow: "hidden",
							}}
						>
							{page.isFirstPage && (
								<>
									<div
										style={{
											fontFamily: "Inter",
											fontWeight: 600,
											fontSize: "13px",
											lineHeight: "1.5em",
											color: "#333333",
											marginBottom: "30px",
											flexShrink: 0,
										}}
									>
										Audio Transcript
									</div>
									<div
										style={{
											fontFamily: "Inter",
											fontWeight: 400,
											fontSize: "12px",
											lineHeight: "2em",
											color: "#333333",
											marginBottom: "30px",
											flexShrink: 0,
										}}
									>
										<div>
											<span style={{ fontWeight: "bold" }}>Course Name:</span> {props.courseName}
										</div>
										<div>
											<span style={{ fontWeight: "bold" }}>Assessment Name:</span> {props.assessmentName}
										</div>
										<div>
											<span style={{ fontWeight: "bold" }}>Student Name:</span> {props.studentName}
										</div>
										<div>
											<span style={{ fontWeight: "bold" }}>Attempt Number:</span> {props.attemptNumber}
										</div>
										<div>
											<span style={{ fontWeight: "bold" }}>Assessment Status:</span> {props.assessmentStatus}
										</div>
										<div>
											<span style={{ fontWeight: "bold" }}>Recording Date:</span> {props.recordingDate}
										</div>
									</div>
								</>
							)}
							<div
								style={{
									fontFamily: "Inter",
									fontWeight: 400,
									fontSize: "12px",
									lineHeight: "1.5em",
									color: "#333333",
									whiteSpace: "pre-wrap",
									wordWrap: "break-word",
									flex: 1,
									overflow: "hidden",
								}}
							>
								{page.content}
							</div>
						</div>

						{pages.length > 1 ? (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									paddingTop: "48px",
									paddingBottom: "24px",
									fontFamily: "Inter",
									fontWeight: 400,
									fontSize: "12px",
									color: "#333333",
									flexShrink: 0,
								}}
							>
								- {page.pageNum} -
							</div>
						) : (
							<div
								style={{
									paddingTop: "48px",
									paddingBottom: "24px",
								}}
							></div>
						)}
					</div>
				))}
			</div>
		</ThemeWrapper>
	);
};

export default AudioTranscriptPDFGenerator;
export { AudioTranscriptReport };
export type { AudioTranscriptReportProps };
